import apiClient from './api.service';

function unwrapBody<T>(res: unknown): T {
  if (res == null || typeof res !== 'object') return res as T;
  const r = res as Record<string, unknown>;
  const inner = r.data;
  if (inner != null && typeof inner === 'object' && 'data' in (inner as object)) {
    return (inner as { data: T }).data;
  }
  if ('data' in r && (r.success === true || r.status === 'success')) {
    return r.data as T;
  }
  return res as T;
}

function unwrapList<T>(res: unknown): T[] {
  const list = unwrapBody<T[] | undefined>(res);
  return Array.isArray(list) ? list : [];
}

// =====================================================
// COMPETENCY TYPES
// =====================================================
export interface CompetencyMatrix {
  id: string;
  company_id: string;
  employee_id: string;
  competency_code: string;
  competency_name: string;
  competency_category?: string;
  required_level: 'Basic' | 'Intermediate' | 'Advanced' | 'Expert' | 'Not Assessed';
  current_level: 'Basic' | 'Intermediate' | 'Advanced' | 'Expert' | 'Not Assessed';
  certification_required: boolean;
  certification_name?: string;
  certification_body?: string;
  certification_number?: string;
  certification_issue_date?: string;
  certification_expiry_date?: string;
  last_assessed_date?: string;
  assessed_by?: string;
  assessment_method?: string;
  assessment_result: 'Competent' | 'Not Competent' | 'Under Assessment';
  is_active: boolean;
  is_compliant: boolean;
  created_at: string;
  updated_at: string;
}

export interface TrainingRecord {
  id: string;
  company_id: string;
  employee_id: string;
  training_code: string;
  training_name: string;
  training_type?: string;
  training_category?: string;
  training_provider?: string;
  trainer_name?: string;
  training_location?: string;
  scheduled_start_date?: string;
  scheduled_end_date?: string;
  actual_start_date?: string;
  actual_end_date?: string;
  training_duration_hours: number;
  status: 'Scheduled' | 'In Progress' | 'Completed' | 'Cancelled' | 'No Show';
  result: 'Pass' | 'Fail' | 'Pending' | 'N/A';
  score?: number;
  grade?: string;
  certificate_issued: boolean;
  certificate_number?: string;
  certificate_issue_date?: string;
  competency_matrix_id?: string;
  training_notes?: string;
  feedback?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
}

export interface CertificationRecord {
  id: string;
  company_id: string;
  employee_id: string;
  certification_code: string;
  certification_name: string;
  certification_type?: string;
  issuing_body: string;
  certificate_number: string;
  issue_date: string;
  expiry_date?: string;
  is_lifetime: boolean;
  status: 'Active' | 'Expired' | 'Renewed' | 'Revoked' | 'Pending';
  renewal_required: boolean;
  renewal_date?: string;
  certificate_document_id?: string;
  supporting_documents?: string[];
  competency_matrix_id?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

// =====================================================
// PERSONNEL COMPETENCY & TRAINING PROCEDURE TYPES
// =====================================================
export type GapStatus = 'Met' | 'Gap' | 'Expiring' | 'Expired';

export interface CompetencyEmployeeRow {
  id: string;
  employee_number: string;
  full_name: string;
  job_title?: string | null;
  join_date?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  confirmation_status?: string | null;
  probation_months?: number | null;
  job_profile_id?: string | null;
  job_profile_code?: string | null;
  job_profile_title?: string | null;
  trainings_completed: number;
  active_certifications: number;
  last_assessment_date?: string | null;
}

export interface JobProfileRequirement {
  id?: string;
  requirement_type: 'Education' | 'Experience' | 'Skill' | 'Competency' | 'Certification' | 'Training';
  name: string;
  competency_code?: string | null;
  required_level?: string | null;
  certification_type?: string | null;
  training_course_id?: string | null;
  course_code?: string | null;
  course_title?: string | null;
  is_mandatory?: boolean | number;
  refresher_months?: number | null;
  notes?: string | null;
}

export interface JobProfile {
  id: string;
  code: string;
  title: string;
  department_id?: string | null;
  department_name?: string | null;
  purpose?: string | null;
  responsibilities?: string | null;
  authorities?: string | null;
  min_education?: string | null;
  min_experience_years?: number | null;
  effective_date?: string | null;
  revision_no?: number;
  is_active: boolean | number;
  requirement_count?: number;
  employee_count?: number;
  requirements?: JobProfileRequirement[];
  employees?: Array<{ id: string; employee_number: string; full_name: string; job_title?: string; department_name?: string; assigned_at?: string }>;
  updated_at?: string;
}

export interface TrainingCourse {
  id: string;
  code: string;
  title: string;
  category: string;
  content?: string | null;
  default_trainer?: string | null;
  frequency?: string | null;
  frequency_months?: number | null;
  duration_hours?: number | null;
  is_induction: boolean | number;
  effectiveness_required: boolean | number;
  is_active: boolean | number;
}

export interface TrainingPlanItem {
  id: string;
  course_id: string;
  course_code: string;
  course_title: string;
  course_category?: string;
  employee_id?: string | null;
  employee_name?: string | null;
  employee_number?: string | null;
  department_id?: string | null;
  department_name?: string | null;
  trainer?: string | null;
  frequency?: string | null;
  planned_months: number[];
  achieved_months: number[];
  status: 'Planned' | 'Completed' | 'Cancelled';
  source_type: string;
  source_ref?: string | null;
  notes?: string | null;
}

export interface TrainingSchedule {
  plan: { id: string; plan_year: number; updated_at?: string; updated_by_name?: string };
  year: number;
  items: TrainingPlanItem[];
  summary: { planned: number; achieved: number; achievement_percent: number | null };
}

export interface TrainingSessionAttendee {
  id: string;
  employee_id: string;
  full_name: string;
  employee_number: string;
  job_title?: string | null;
  department_name?: string | null;
  attendance_status?: 'Present' | 'Absent' | 'Excused' | null;
  status: string;
  result: string;
  plan_item_id?: string | null;
}

export interface TrainingSession {
  id: string;
  session_number: string;
  course_id: string;
  course_code?: string;
  course_title?: string;
  course_content?: string | null;
  effectiveness_required?: number | boolean;
  topic?: string | null;
  session_date: string;
  start_time?: string | null;
  end_time?: string | null;
  trainer_type: 'Internal' | 'External';
  trainer_employee_id?: string | null;
  trainer_name?: string | null;
  trainer_organisation?: string | null;
  trainer_display?: string | null;
  location?: string | null;
  trainer_observations?: string | null;
  status: 'Planned' | 'Completed' | 'Cancelled';
  attendee_count?: number;
  present_count?: number;
  evaluation_id?: string | null;
  evaluation_status?: string | null;
  evaluation_due_date?: string | null;
  attendees?: TrainingSessionAttendee[];
}

export interface EffectivenessLine {
  id: string;
  employee_id: string;
  employee_name: string;
  employee_number?: string;
  job_title?: string | null;
  method?: 'A' | 'B' | 'C' | 'D' | null;
  score?: number | null;
  evaluator_employee_id?: string | null;
  evaluator_name?: string | null;
  evaluator_display?: string | null;
  evaluated_at?: string | null;
  remarks?: string | null;
  follow_up: 'None' | 'Retrain' | 'Supervised work';
}

export interface EffectivenessEvaluation {
  id: string;
  session_id: string;
  session_number: string;
  session_date: string;
  course_title: string;
  course_code?: string;
  trainer_display?: string | null;
  due_date: string;
  overall_effective?: number | boolean | null;
  comments?: string | null;
  delay_justification?: string | null;
  status: 'Open' | 'Completed' | 'Acknowledged';
  completed_at?: string | null;
  hr_ack_at?: string | null;
  hr_ack_by_name?: string | null;
  hr_ack_notes?: string | null;
  overdue?: boolean;
  trainee_count?: number;
  scored_count?: number;
  lines?: EffectivenessLine[];
}

export type AssessmentScoreKey =
  | 'score_job_knowledge'
  | 'score_attendance'
  | 'score_teamwork'
  | 'score_communication'
  | 'score_productivity'
  | 'score_suggestions'
  | 'score_compliance'
  | 'score_attitude';

export interface CompetencyAssessment extends Partial<Record<AssessmentScoreKey, number | null>> {
  id: string;
  assessment_number: string;
  employee_id: string;
  employee_name: string;
  employee_number?: string;
  job_title?: string | null;
  department_name?: string | null;
  join_date?: string | null;
  confirmation_status?: string | null;
  assessment_type: string;
  assessment_type_other?: string | null;
  cycle_id?: string | null;
  assessment_date?: string | null;
  evaluator_employee_id?: string | null;
  evaluator_name?: string | null;
  total_score?: number | null;
  grade?: string | null;
  comments?: string | null;
  training_requirements?: string | null;
  recommendation?: string | null;
  status: 'Draft' | 'Submitted' | 'Closed';
  hr_outcome?: string | null;
  hr_notes?: string | null;
  hr_closed_at?: string | null;
}

export interface AssessmentCycle {
  id: string;
  cycle_year: number;
  cutoff_date: string;
  status: 'Open' | 'Closed';
  total: number;
  closed: number;
}

export interface GapMatrix {
  columns: Array<{ key: string; requirement_type: string; name: string }>;
  rows: Array<
    CompetencyEmployeeRow & {
      overall: GapStatus;
      cells: Record<string, { status: GapStatus; detail: string; mandatory: boolean; requirement_id: string }>;
    }
  >;
  summary: Record<GapStatus, number>;
}

export interface CompetencyEmployeeFile {
  employee: {
    id: string;
    employee_number: string;
    full_name: string;
    job_title?: string | null;
    join_date?: string | null;
    probation_months?: number | null;
    confirmation_status?: string | null;
    confirmation_date?: string | null;
    employment_status?: string | null;
    department_name?: string | null;
    reporting_to_name?: string | null;
  };
  competencies: CompetencyMatrix[];
  trainings: TrainingRecord[];
  certifications: CertificationRecord[];
  gap: { job_profile: { id: string; code: string; title: string } | null; items: Array<{ key: string; requirement_type: string; name: string; status: GapStatus; detail: string; mandatory: boolean }> };
  assessments: CompetencyAssessment[];
  qualifications: Array<{ id: string; qual_type: string; title: string; institution?: string; level_or_grade?: string; issued_on?: string; expiry_date?: string; is_verified?: number }>;
}

export type CompetencyImportKind =
  | 'job-profiles'
  | 'assignments'
  | 'courses'
  | 'schedule'
  | 'training-records'
  | 'certifications'
  | 'competencies'
  | 'assessments';

export interface CompetencyImportResult {
  kind: CompetencyImportKind;
  dry_run: boolean;
  total: number;
  valid: number;
  created: number;
  updated: number;
  errors: Array<{ row: number; message: string }>;
}

export type AttendeeSelector = {
  employee_ids?: string[];
  department_ids?: string[];
  job_profile_ids?: string[];
  plan_item_ids?: string[];
};

type Query = Record<string, string | number | boolean | undefined | null>;
const clean = (q?: Query) =>
  q ? Object.fromEntries(Object.entries(q).filter(([, v]) => v !== undefined && v !== null && v !== '')) : undefined;

// =====================================================
// COMPETENCY SERVICE
// =====================================================
class CompetencyService {
  // ---------- employees
  async overview(q?: Query): Promise<CompetencyEmployeeRow[]> {
    return unwrapList<CompetencyEmployeeRow>(await apiClient.get('/competency/overview', { params: clean(q) }));
  }

  /** Direct reports and department members of the signed-in user (no competency permission needed). */
  async myTeam(): Promise<CompetencyEmployeeRow[]> {
    return unwrapList<CompetencyEmployeeRow>(await apiClient.get('/competency/my-team'));
  }

  async employeeFile(employeeId: string): Promise<CompetencyEmployeeFile> {
    return unwrapBody<CompetencyEmployeeFile>(await apiClient.get(`/competency/employees/${employeeId}/file`));
  }

  async updateTraining(id: string, data: Partial<TrainingRecord>) {
    return unwrapBody<TrainingRecord>(await apiClient.put(`/competency/trainings/${id}`, data));
  }

  async deleteTraining(id: string) {
    await apiClient.delete(`/competency/trainings/${id}`);
  }

  async updateCertification(id: string, data: Partial<CertificationRecord>) {
    return unwrapBody<CertificationRecord>(await apiClient.put(`/competency/certifications/${id}`, data));
  }

  async deleteCertification(id: string) {
    await apiClient.delete(`/competency/certifications/${id}`);
  }

  async deleteCompetency(id: string) {
    await apiClient.delete(`/competency/competencies/${id}`);
  }

  // ---------- job descriptions
  async listJobProfiles(q?: Query): Promise<JobProfile[]> {
    return unwrapList<JobProfile>(await apiClient.get('/competency/job-profiles', { params: clean(q) }));
  }

  async getJobProfile(id: string): Promise<JobProfile> {
    return unwrapBody<JobProfile>(await apiClient.get(`/competency/job-profiles/${id}`));
  }

  async saveJobProfile(id: string | null, data: Partial<JobProfile>): Promise<JobProfile> {
    const res = id ? await apiClient.put(`/competency/job-profiles/${id}`, data) : await apiClient.post('/competency/job-profiles', data);
    return unwrapBody<JobProfile>(res);
  }

  async deleteJobProfile(id: string) {
    await apiClient.delete(`/competency/job-profiles/${id}`);
  }

  async assignJobProfile(id: string, employeeIds: string[]) {
    return unwrapBody(await apiClient.post(`/competency/job-profiles/${id}/assign`, { employee_ids: employeeIds }));
  }

  async unassignJobProfile(employeeIds: string[]) {
    return unwrapBody(await apiClient.post('/competency/job-profiles/unassign', { employee_ids: employeeIds }));
  }

  // ---------- courses
  async listCourses(q?: Query): Promise<TrainingCourse[]> {
    return unwrapList<TrainingCourse>(await apiClient.get('/competency/courses', { params: clean(q) }));
  }

  async saveCourse(id: string | null, data: Partial<TrainingCourse>): Promise<TrainingCourse> {
    const res = id ? await apiClient.put(`/competency/courses/${id}`, data) : await apiClient.post('/competency/courses', data);
    return unwrapBody<TrainingCourse>(res);
  }

  async deleteCourse(id: string) {
    await apiClient.delete(`/competency/courses/${id}`);
  }

  // ---------- annual schedule
  async getSchedule(year: number, q?: Query): Promise<TrainingSchedule> {
    return unwrapBody<TrainingSchedule>(await apiClient.get('/competency/schedule', { params: clean({ year, ...q }) }));
  }

  async addScheduleItems(data: Record<string, unknown>) {
    return unwrapBody<{ created: number }>(await apiClient.post('/competency/schedule/items', data));
  }

  async updateScheduleItem(id: string, data: Record<string, unknown>) {
    return unwrapBody(await apiClient.put(`/competency/schedule/items/${id}`, data));
  }

  async deleteScheduleItem(id: string) {
    await apiClient.delete(`/competency/schedule/items/${id}`);
  }

  async requestTrainingLookups(): Promise<{
    courses: Array<{ id: string; code: string; title: string }>;
    employees: Array<{ id: string; employee_number: string; full_name: string; department_id?: string | null; department_name?: string | null }>;
  }> {
    return unwrapBody(await apiClient.get('/competency/schedule/request-lookups'));
  }

  async requestTraining(data: {
    source_type: 'NCR' | 'CAPA' | 'MOC';
    source_id: string;
    course_id: string;
    employee_ids?: string[];
    department_id?: string | null;
    planned_months?: number[];
    notes?: string | null;
  }) {
    return unwrapBody<{ created: number }>(await apiClient.post('/competency/schedule/request', data));
  }

  // ---------- sessions
  async listSessions(q?: Query): Promise<TrainingSession[]> {
    return unwrapList<TrainingSession>(await apiClient.get('/competency/sessions', { params: clean(q) }));
  }

  async getSession(id: string): Promise<TrainingSession> {
    return unwrapBody<TrainingSession>(await apiClient.get(`/competency/sessions/${id}`));
  }

  async createSession(data: Record<string, unknown>): Promise<TrainingSession> {
    return unwrapBody<TrainingSession>(await apiClient.post('/competency/sessions', data));
  }

  async updateSession(id: string, data: Record<string, unknown>): Promise<TrainingSession> {
    return unwrapBody<TrainingSession>(await apiClient.put(`/competency/sessions/${id}`, data));
  }

  async addAttendees(id: string, sel: AttendeeSelector) {
    return unwrapBody(await apiClient.post(`/competency/sessions/${id}/attendees`, sel));
  }

  async removeAttendee(id: string, recordId: string) {
    await apiClient.delete(`/competency/sessions/${id}/attendees/${recordId}`);
  }

  async setAttendance(id: string, marks: Array<{ id: string; attendance_status: string }>) {
    return unwrapBody(await apiClient.put(`/competency/sessions/${id}/attendance`, { marks }));
  }

  async completeSession(id: string, trainer_observations?: string | null) {
    return unwrapBody(await apiClient.post(`/competency/sessions/${id}/complete`, { trainer_observations }));
  }

  async cancelSession(id: string) {
    return unwrapBody(await apiClient.post(`/competency/sessions/${id}/cancel`, {}));
  }

  // ---------- effectiveness
  async listEffectiveness(q?: Query): Promise<EffectivenessEvaluation[]> {
    return unwrapList<EffectivenessEvaluation>(await apiClient.get('/competency/effectiveness', { params: clean(q) }));
  }

  async getEffectiveness(id: string): Promise<EffectivenessEvaluation> {
    return unwrapBody<EffectivenessEvaluation>(await apiClient.get(`/competency/effectiveness/${id}`));
  }

  async saveEffectivenessLines(id: string, lines: Array<Partial<EffectivenessLine> & { id: string }>) {
    return unwrapBody(await apiClient.put(`/competency/effectiveness/${id}/lines`, { lines }));
  }

  async completeEffectiveness(id: string, data: { overall_effective: boolean; comments?: string | null; delay_justification?: string | null }) {
    return unwrapBody(await apiClient.post(`/competency/effectiveness/${id}/complete`, data));
  }

  async acknowledgeEffectiveness(id: string, notes?: string | null) {
    return unwrapBody(await apiClient.post(`/competency/effectiveness/${id}/acknowledge`, { notes }));
  }

  // ---------- assessments
  async listAssessments(q?: Query): Promise<CompetencyAssessment[]> {
    return unwrapList<CompetencyAssessment>(await apiClient.get('/competency/assessments', { params: clean(q) }));
  }

  async getAssessment(id: string): Promise<CompetencyAssessment> {
    return unwrapBody<CompetencyAssessment>(await apiClient.get(`/competency/assessments/${id}`));
  }

  async saveAssessment(id: string | null, data: Record<string, unknown>): Promise<CompetencyAssessment> {
    const res = id ? await apiClient.put(`/competency/assessments/${id}`, data) : await apiClient.post('/competency/assessments', data);
    return unwrapBody<CompetencyAssessment>(res);
  }

  async submitAssessment(id: string) {
    return unwrapBody<CompetencyAssessment>(await apiClient.post(`/competency/assessments/${id}/submit`, {}));
  }

  async returnAssessment(id: string, reason: string) {
    return unwrapBody<CompetencyAssessment>(await apiClient.post(`/competency/assessments/${id}/return`, { reason }));
  }

  async closeAssessment(id: string, data: { hr_notes?: string | null; confirmation_date?: string | null; training_course_ids?: string[] }) {
    return unwrapBody<CompetencyAssessment>(await apiClient.post(`/competency/assessments/${id}/close`, data));
  }

  async deleteAssessment(id: string) {
    await apiClient.delete(`/competency/assessments/${id}`);
  }

  async listCycles(): Promise<AssessmentCycle[]> {
    return unwrapList<AssessmentCycle>(await apiClient.get('/competency/assessment-cycles'));
  }

  async generateCycle(year: number) {
    return unwrapBody<{ cycle_id: string; eligible: number; created: number }>(await apiClient.post('/competency/assessment-cycles', { year }));
  }

  async closeCycle(id: string) {
    return unwrapBody(await apiClient.post(`/competency/assessment-cycles/${id}/close`, {}));
  }

  // ---------- gap matrix and import
  async gapMatrix(q?: Query): Promise<GapMatrix> {
    return unwrapBody<GapMatrix>(await apiClient.get('/competency/gap-matrix', { params: clean(q) }));
  }

  async downloadImportTemplate(kind: CompetencyImportKind): Promise<Blob> {
    return (await apiClient.get(`/competency/import/${kind}/template`, { responseType: 'blob' })) as unknown as Blob;
  }

  async exportJobProfiles(): Promise<Blob> {
    return (await apiClient.get('/competency/job-profiles/export', { responseType: 'blob' })) as unknown as Blob;
  }

  async runImport(kind: CompetencyImportKind, file: File, dryRun: boolean): Promise<CompetencyImportResult> {
    const form = new FormData();
    form.append('file', file);
    const res = await apiClient.post(`/competency/import/${kind}?dry_run=${dryRun ? 1 : 0}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrapBody<CompetencyImportResult>(res);
  }

  async getEmployeeCompetencies(employeeId: string): Promise<CompetencyMatrix[]> {
    const response = await apiClient.get(`/competency/employees/${employeeId}/competencies`);
    return unwrapList<CompetencyMatrix>(response);
  }

  async upsertCompetency(data: Partial<CompetencyMatrix>): Promise<CompetencyMatrix> {
    const response = await apiClient.post('/competency/competencies', data);
    return unwrapBody<CompetencyMatrix>(response);
  }

  async getEmployeeTrainings(employeeId: string): Promise<TrainingRecord[]> {
    const response = await apiClient.get(`/competency/employees/${employeeId}/trainings`);
    return unwrapList<TrainingRecord>(response);
  }

  async createTraining(data: Partial<TrainingRecord>): Promise<TrainingRecord> {
    const response = await apiClient.post('/competency/trainings', data);
    return unwrapBody<TrainingRecord>(response);
  }

  async getEmployeeCertifications(employeeId: string): Promise<CertificationRecord[]> {
    const response = await apiClient.get(`/competency/employees/${employeeId}/certifications`);
    return unwrapList<CertificationRecord>(response);
  }

  async createCertification(data: Partial<CertificationRecord>): Promise<CertificationRecord> {
    const response = await apiClient.post('/competency/certifications', data);
    return unwrapBody<CertificationRecord>(response);
  }
}

export const competencyService = new CompetencyService();

